// SetPasswordPage.jsx
import React, { useState, useEffect } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';
import { InputField, Button, CircularLoader, NoticeBox } from '@dhis2/ui';
import { useNavigate, useLocation } from 'react-router-dom';

const SetPasswordPage = () => {
  const engine = useDataEngine();
  const navigate = useNavigate(); 
  const location = useLocation();
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('validating'); // validating, setting, success

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const inviteToken = searchParams.get('token');
    const userEmail = searchParams.get('email');

    if (!inviteToken || !userEmail) {
      setError('Invalid invitation link.');
      setStep('error');
      return;
    }

    setToken(inviteToken);
    setEmail(userEmail);
    fetchUserData(inviteToken);
  }, [location]);

  const fetchUserData = async (token) => {
    try {
      const res = await engine.query({
        user: {
          resource: `api/users/invite/${token}`,
          params: { fields: 'username,firstName,surname,email' }
        }
      });
      setUser(res.user);
      setStep('setting');
    } catch (err) {
      console.error('Token validation failed:', err);
      setError('Invitation link is invalid or expired.');
      setStep('error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await engine.mutate({
        type: 'create',
        resource: 'auth/invite',
        data: {
          token,
          email,
          password,
          confirmPassword,
          // Optional: firstName, surname if not pre-filled
        }
      });

      setStep('success');
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login'); // or your HR dashboard
      }, 2000);
    } catch (err) {
      console.error('Password setup failed:', err);
      setError(err.message || 'Failed to set password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'validating') {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <CircularLoader large />
        <p>Validating your invitation...</p>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div style={{ padding: '2rem' }}>
        <NoticeBox critical>{error}</NoticeBox>
        <Button onClick={() => navigate('/login')}>Go to Login</Button>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <NoticeBox success>Your password has been set! Redirecting to login...</NoticeBox>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '400px', margin: '2rem auto', padding: '1rem' }}>
      <h2>Set Your Password</h2>
      {user && (
        <p>
          Hello <strong>{user.firstName} {user.surname}</strong> ({user.email})
        </p>
      )}
      <form onSubmit={handleSubmit}>
        <InputField
          label="New Password"
          type="password"
          value={password}
          onChange={({ value }) => setPassword(value)}
          required
        />
        <InputField
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={({ value }) => setConfirmPassword(value)}
          required
        />
        {error && <NoticeBox critical>{error}</NoticeBox>}
        <Button primary block type="submit" loading={loading}>
          Set Password
        </Button>
      </form>
    </div>
  );
};

export default SetPasswordPage;